import React, { memo } from 'react';
import { Handle, Position } from 'reactflow';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';

// HubSpot "sprocket" SVG — matches brand identity
const HubSpotIcon = ({ className }: { className?: string }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
        <path d="M18.164 7.931V5.085a2.085 2.085 0 0 0 1.205-1.885 2.085 2.085 0 1 0-4.17 0c0 .838.498 1.56 1.205 1.885v2.846a5.924 5.924 0 0 0-2.698 1.118L7.07 4.721a2.32 2.32 0 1 0-.87 1.309l6.466 4.295a5.91 5.91 0 0 0-.814 3.02 5.913 5.913 0 0 0 .964 3.254l-1.968 1.968a1.745 1.745 0 1 0 1.24 1.176l1.952-1.952a5.934 5.934 0 0 0 3.696 1.292 5.944 5.944 0 1 0-1.572-11.152zm1.572 8.402a3.195 3.195 0 1 1 0-6.39 3.195 3.195 0 0 1 0 6.39z"/>
    </svg>
);

const OBJECT_CONFIG: Record<string, { label: string; color: string }> = {
    contacts:   { label: 'Contacts',   color: '#ff7a59' },
    deals:      { label: 'Deals',      color: '#00bda5' },
    companies:  { label: 'Companies',  color: '#f8c823' },
    tickets:    { label: 'Tickets',    color: '#516f90' },
    products:   { label: 'Products',   color: '#6a78d1' },
    line_items: { label: 'Line Items', color: '#e66e50' },
};

const OP_LABEL: Record<string, string> = {
    fetch:  'Fetch All',
    list:   'Fetch All',
    search: 'Search',
    create: 'Create',
    update: 'Update',
};

interface HubSpotNodeProps {
    data: {
        label?: string;
        object?: string;
        operation?: string;
        properties?: string[];
        filters?: any[];
        executionStatus?: 'Running' | 'Success' | 'Failed';
    };
    selected?: boolean;
}

function HubSpotNode({ data, selected }: HubSpotNodeProps) {
    const obj = (data.object || 'contacts').toLowerCase();
    const cfg = OBJECT_CONFIG[obj] || OBJECT_CONFIG.contacts;
    const opLabel = OP_LABEL[(data.operation || 'fetch').toLowerCase()] || data.operation;
    const propCount = Array.isArray(data.properties) ? data.properties.length : 0;
    const filterCount = Array.isArray(data.filters) ? data.filters.length : 0;
    const label = data.label || cfg.label;

    const statusIcon =
        data.executionStatus === 'Running' ? <Loader2 className="h-3 w-3 animate-spin text-white" /> :
        data.executionStatus === 'Success' ? <CheckCircle2 className="h-3 w-3 text-white" /> :
        data.executionStatus === 'Failed'  ? <XCircle className="h-3 w-3 text-white" /> : null;

    return (
        <div
            className={`relative rounded-xl overflow-hidden shadow-md transition-all duration-200 ${
                selected ? 'ring-2 ring-offset-1 shadow-lg' : 'hover:shadow-lg'
            }`}
            style={{
                width: 172,
                minHeight: 80,
                fontFamily: 'Inter, system-ui, sans-serif',
                // @ts-ignore
                '--tw-ring-color': cfg.color,
            }}
        >
            {/* Gradient header */}
            <div
                className="px-3 py-2 flex items-center gap-2"
                style={{ background: `linear-gradient(135deg, ${cfg.color} 0%, ${cfg.color}cc 100%)` }}
            >
                <div className="p-1.5 bg-white/20 rounded-lg flex-shrink-0">
                    <HubSpotIcon className="h-4 w-4 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-white text-[9px] font-bold uppercase tracking-widest opacity-80">
                        HubSpot · {cfg.label}
                    </div>
                    <div className="text-white text-xs font-bold truncate leading-tight mt-0.5" title={label}>
                        {label}
                    </div>
                </div>
                {statusIcon && <div className="flex-shrink-0">{statusIcon}</div>}
            </div>

            {/* Stats footer */}
            <div className="bg-white px-3 py-1.5 flex items-center gap-2 text-[10px] text-gray-500 flex-wrap">
                <span
                    className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-bold text-white"
                    style={{ background: cfg.color }}
                >
                    {opLabel}
                </span>
                {propCount > 0 && (
                    <span className="text-gray-500">{propCount} fields</span>
                )}
                {filterCount > 0 && (
                    <span className="text-gray-400">{filterCount} filter{filterCount !== 1 ? 's' : ''}</span>
                )}
                {propCount === 0 && filterCount === 0 && (
                    <span className="italic text-gray-400">Not configured</span>
                )}
            </div>

            {/* Output handle */}
            <Handle
                type="source"
                position={Position.Right}
                id="out"
                style={{ background: cfg.color, width: 10, height: 10, border: '2px solid white', right: -5 }}
            />
            <Handle
                type="source"
                position={Position.Bottom}
                id="out-bottom"
                style={{ background: cfg.color, width: 8, height: 8, border: '2px solid white', bottom: -4 }}
            />
        </div>
    );
}

export default memo(HubSpotNode);
